import { ref, useContext } from '@nuxtjs/composition-api';

import customerAddressCreate from './customerAddressCreate';
import customerAddressDelete from './customerAddressDelete';
import customerAddressUpdate from './customerAddressUpdate';
import { CustomerAddress, CustomerAddressInput } from './useAddress.types';

export interface UseAddressErrors {
  create: string | null;
  update: string | null;
  delete: string | null;
}

const addressToRemove = ref<CustomerAddress | null>(null);

// eslint-disable-next-line max-lines-per-function
const useAddress = () => {
  const isLoading = ref(false);
  const errors = ref<UseAddressErrors>({ create: null, update: null, delete: null });

  const { $config } = useContext();

  const handleCustomerAddressCreate = async ({
    accessToken,
    address,
  }: {
    accessToken: string;
    address: CustomerAddressInput;
  }) => {
    try {
      errors.value.create = null;
      isLoading.value = true;

      return await customerAddressCreate({
        accessToken,
        customerAccountEndpoint: $config.customerAccountEndpoint,
        address,
      });
    } catch (err) {
      errors.value = {
        ...errors.value,
        create: err instanceof Error ? err.message : String(err),
      };
    } finally {
      isLoading.value = false;
    }
  };

  const handleCustomerAddressDelete = async ({
    accessToken,
    addressId,
  }: {
    accessToken: string;
    addressId: string;
  }) => {
    try {
      errors.value.delete = null;
      isLoading.value = true;

      return await customerAddressDelete({
        accessToken,
        customerAccountEndpoint: $config.customerAccountEndpoint,
        addressId,
      });
    } catch (err) {
      errors.value = {
        ...errors.value,
        delete: err instanceof Error ? err.message : String(err),
      };
    } finally {
      isLoading.value = false;
    }
  };

  const handleCustomerAddressUpdate = async ({
    accessToken,
    address,
    addressId,
    defaultAddress,
  }: {
    accessToken: string;
    address?: CustomerAddressInput;
    addressId: string;
    defaultAddress?: boolean;
  }) => {
    try {
      errors.value.update = null;
      isLoading.value = true;

      return await customerAddressUpdate({
        accessToken,
        customerAccountEndpoint: $config.customerAccountEndpoint,
        address,
        addressId,
        defaultAddress,
      });
    } catch (err) {
      errors.value = {
        ...errors.value,
        update: err instanceof Error ? err.message : String(err),
      };
    } finally {
      isLoading.value = false;
    }
  };

  const setAddressToRemove = (address: CustomerAddress | null) => {
    addressToRemove.value = address;
  };

  return {
    addressToRemove,
    setAddressToRemove,
    customerAddressCreate: handleCustomerAddressCreate,
    customerAddressDelete: handleCustomerAddressDelete,
    customerAddressUpdate: handleCustomerAddressUpdate,
    errors,
    isLoading,
  };
};

export default useAddress;
